import React, { useState, useEffect } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import {
  Button,
  CssBaseline,
  TextField,
  Link,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { useTheme } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";

import WhatsAppIconWhite from "../../assets/duo-lockup-horizontal-dark.svg";
import WhatsAppIcon from "../../assets/duo-lockup-horizontal-light.svg";
import WhatsAppIconBar from "../../assets/duo-lockup-bar.svg";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    minHeight: "100vh",
    overflow: "hidden",
  },
  brandPanel: {
    flex: "0 0 45%",
    background: theme.palette.type === "dark" ? "#0F1E36" : "#2D7BE8",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(6),
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  decorCircle1: {
    position: "absolute",
    width: 320,
    height: 320,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.06)",
    top: -80,
    right: -80,
  },
  decorCircle2: {
    position: "absolute",
    width: 220,
    height: 220,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.05)",
    bottom: 40,
    left: -60,
  },
  decorCircle3: {
    position: "absolute",
    width: 120,
    height: 120,
    borderRadius: "50%",
    background: "rgba(255,255,255,0.08)",
    bottom: 180,
    right: 40,
  },
  brandContent: {
    position: "relative",
    zIndex: 1,
    textAlign: "center",
    maxWidth: 360,
  },
  brandLogo: {
    height: 72,
    width: "auto",
    marginBottom: theme.spacing(4),
    filter: "drop-shadow(0px 4px 12px rgba(0,0,0,0.2))",
  },
  brandTagline: {
    color: "#ffffff",
    fontWeight: 700,
    fontSize: "1.75rem",
    lineHeight: 1.3,
    marginBottom: theme.spacing(2),
  },
  brandSubtitle: {
    color: "rgba(255,255,255,0.75)",
    fontSize: "0.95rem",
    lineHeight: 1.6,
  },
  brandDivider: {
    width: 48,
    height: 3,
    background: "rgba(255,255,255,0.4)",
    borderRadius: 2,
    margin: `${theme.spacing(3)}px auto`,
  },
  formPanel: {
    flex: "0 0 55%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    backgroundColor: theme.palette.login,
    [theme.breakpoints.down("sm")]: {
      flex: "0 0 100%",
    },
  },
  formWrapper: {
    width: "100%",
    maxWidth: 400,
  },
  formHeader: {
    marginBottom: theme.spacing(4),
  },
  formTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  formSubtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  mobileLogo: {
    display: "none",
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
  },
  mobileLogoImg: {
    height: 48,
    width: "auto",
  },
  form: {
    width: "100%",
  },
  submitButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    fontWeight: 600,
    fontSize: "1rem",
    borderRadius: theme.spacing(1),
    boxShadow: "0px 4px 14px rgba(26, 115, 215, 0.4)",
    textTransform: "none",
  },
  backLink: {
    display: "block",
    textAlign: "center",
    fontSize: "0.875rem",
    fontWeight: 500,
  },
  errorBox: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(211, 47, 47, 0.15)"
        : "#fdecea",
    border: "1px solid #d32f2f",
    marginBottom: theme.spacing(2),
  },
  errorText: {
    color: theme.palette.type === "dark" ? "#ef9a9a" : "#d32f2f",
    fontSize: "0.875rem",
  },
}));

const ResetPassword = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isDarkMode = theme.palette.type === "dark";
  const history = useHistory();

  const [token, setToken] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invalidToken, setInvalidToken] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get("token");
    if (!t) {
      setInvalidToken(true);
    } else {
      setToken(t);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast.error("A senha deve ter no mínimo 6 caracteres.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("As senhas não coincidem.");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Senha redefinida com sucesso!");
      history.push("/login");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Token inválido ou expirado. Solicite um novo link.";
      toast.error(msg);
      if (err?.response?.status === 400) {
        setInvalidToken(true);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <CssBaseline />

      <div className={classes.brandPanel}>
        <div className={classes.decorCircle1} />
        <div className={classes.decorCircle2} />
        <div className={classes.decorCircle3} />

        <div className={classes.brandContent}>
          <img
            src={isDarkMode ? WhatsAppIconWhite : WhatsAppIconBar}
            alt="Message Express"
            className={classes.brandLogo}
          />
          <div className={classes.brandDivider} />
          <Typography variant="h5" className={classes.brandTagline}>
            Atendimento inteligente, resultados reais.
          </Typography>
          <Typography variant="body2" className={classes.brandSubtitle}>
            Gerencie conversas, equipes e campanhas em um só lugar — com a
            agilidade que o seu negócio precisa.
          </Typography>
        </div>
      </div>

      <div className={classes.formPanel}>
        <div className={classes.formWrapper}>
          <div className={classes.mobileLogo}>
            <img
              src={isDarkMode ? WhatsAppIconWhite : WhatsAppIcon}
              alt="Message Express"
              className={classes.mobileLogoImg}
            />
          </div>

          <div className={classes.formHeader}>
            <Typography variant="h5" className={classes.formTitle}>
              Redefinir senha
            </Typography>
            <Typography variant="body2" className={classes.formSubtitle}>
              {invalidToken
                ? "Link inválido ou expirado"
                : "Crie uma nova senha para sua conta"}
            </Typography>
          </div>

          {invalidToken ? (
            <>
              <div className={classes.errorBox}>
                <Typography className={classes.errorText}>
                  Este link de redefinição é inválido ou já expirou. Links são
                  válidos por 1 hora após o envio.
                </Typography>
              </div>
              <Link
                component={RouterLink}
                to="/forgot-password"
                variant="body2"
                color="primary"
                className={classes.backLink}
                style={{ marginBottom: 12 }}
              >
                Solicitar novo link
              </Link>
              <br />
              <Link
                component={RouterLink}
                to="/login"
                variant="body2"
                color="primary"
                className={classes.backLink}
              >
                Voltar para o login
              </Link>
            </>
          ) : (
            <form className={classes.form} noValidate onSubmit={handleSubmit}>
              <TextField
                variant="outlined"
                fullWidth
                label="Nova senha"
                name="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type={showPassword ? "text" : "password"}
                required
                autoFocus
                inputProps={{ minLength: 6 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((p) => !p)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                variant="outlined"
                fullWidth
                label="Confirmar nova senha"
                name="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                type={showConfirm ? "text" : "password"}
                required
                style={{ marginTop: 16 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirm((p) => !p)}
                        edge="end"
                      >
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submitButton}
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading ? "Salvando..." : "Redefinir senha"}
              </Button>

              <Link
                component={RouterLink}
                to="/login"
                variant="body2"
                color="primary"
                className={classes.backLink}
              >
                Voltar para o login
              </Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
